import * as React from "react";
import {
  Banner,
  Button,
  Card,
  CardBody,
  Input,
  PageContainer,
  Select,
} from "../components";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { useAsync, useAction } from "../general/useAsync";
import * as store from "./CurriculumReportingStore";

const kinds = [
  "Module",
  "Qualification",
  "QualificationLevel",
  "QualificationProgramLevel",
  "QualificationModule",
  "Assessment",
  "AssessmentGroup",
  "MarkType",
  "AssessmentType",
  "AssessmentGroupType",
  "CalculationProcedure",
];

const blankMapping: store.CurriculumMappingDraft = {
  standardID: "",
  standardVersion: "",
  gradeCode: "",
  subjectCode: "",
  outcomeCode: null,
  templateKey: null,
  templateItemID: null,
  templateRevision: null,
  expectedVersion: 0,
};

const blankMeasure: store.ReportingMeasureDraft = {
  name: "",
  qualificationModuleID: "",
  entityType: "Assessment",
  entityID: "",
  markTypeID: "",
  comparisonKind: "LocalAttainment",
  comparisonDefinitionID: "",
  comparisonVersion: "1",
  finalizedOnly: true,
  moderation: "Any",
  enabled: true,
};

export default function CurriculumReportingSetup() {
  const canMap = usePermission(Permissions.CONFIGURATION_WRITE);
  const canReadReports = usePermission(Permissions.REPORTS_READ);
  const canWriteReports = usePermission(Permissions.REPORTS_WRITE);
  const [kind, setKind] = React.useState("Assessment");
  const [entityID, setEntityID] = React.useState("");
  const [mapping, setMapping] = React.useState({ ...blankMapping });
  const [message, setMessage] = React.useState("");
  const rows = useAsync(() => store.entities(kind), [kind]);
  const [measureID, setMeasureID] = React.useState<string | null>(null);
  const [measure, setMeasure] = React.useState({ ...blankMeasure });
  const [measureDirty, setMeasureDirty] = React.useState(false);
  const measures = useAsync(store.measures, [], { enabled: canReadReports });
  const offerings = useAsync(() => store.entities("QualificationModule"), []);
  const markTypes = useAsync(() => store.entities("MarkType"), []);
  const targets = useAsync(
    () => store.entities(measure.entityType),
    [measure.entityType],
  );
  const history = useAsync(() => store.snapshots(measureID!), [measureID], {
    enabled: !!measureID && canReadReports,
  });
  React.useEffect(() => {
    setEntityID("");
    setMapping({ ...blankMapping });
  }, [kind]);

  function chooseEntity(id: string) {
    setEntityID(id);
    const entity = rows.data?.find((row) => row.id === id);
    setMapping(
      entity
        ? {
            standardID: entity.curriculum.standardID ?? "",
            standardVersion: entity.curriculum.standardVersion ?? "",
            gradeCode: entity.curriculum.gradeCode ?? "",
            subjectCode: entity.curriculum.subjectCode ?? "",
            outcomeCode: entity.curriculum.outcomeCode,
            templateKey: entity.templateKey,
            templateItemID: entity.templateItemID,
            templateRevision: entity.templateRevision,
            expectedVersion: entity.version,
          }
        : { ...blankMapping },
    );
  }

  const saveMapping = useAction(async () => {
    const result = await store.saveMapping(kind, entityID, mapping);
    setMapping((current) => ({ ...current, expectedVersion: result.version }));
    rows.refresh();
    targets.refresh();
    setMessage("Curriculum mapping saved.");
  });

  const saveMeasure = useAction(async () => {
    const result = await store.saveMeasure(measureID, measure);
    setMeasureID(result.id);
    setMeasure({ ...result, expectedVersion: result.version });
    setMeasureDirty(false);
    measures.refresh();
    history.refresh();
    setMessage(
      "Reporting measure saved. Enabled measures publish stored results automatically.",
    );
  });

  function chooseMeasure(id: string) {
    const row = measures.data?.find((item) => item.id === id);
    setMeasureID(row?.id ?? null);
    setMeasure(
      row ? { ...row, expectedVersion: row.version } : { ...blankMeasure },
    );
    setMeasureDirty(false);
    setMessage("");
  }

  const errors = [
    rows.error,
    offerings.error,
    markTypes.error,
    targets.error,
    canReadReports ? measures.error : "",
    history.error,
    saveMapping.error,
    saveMeasure.error,
  ].filter(Boolean);

  return (
    <PageContainer>
      <h1 className="mb-2 text-2xl font-semibold">
        Curriculum reporting setup
      </h1>
      <p className="mb-6 text-sm text-text-weak">
        Map local assessment records to shared curriculum definitions, choose
        one stored result, and automatically publish a reporting snapshot.
        Template provenance and curriculum versions are recorded separately.
      </p>
      {errors.map((error, index) => (
        <Banner key={index}>{error}</Banner>
      ))}
      {message && (
        <p role="status" className="my-3 text-success-text">
          {message}
        </p>
      )}
      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardBody>
            <h2 className="mb-4 text-lg font-semibold">Curriculum mapping</h2>
            <p className="mb-4 text-sm text-text-weak">
              The Grade 5 Mathematics reporting proof is available in the
              curriculum template wizard. It uses a demonstration curriculum.
              Existing rows require an explicit mapping.
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveMapping.run();
              }}
            >
              <label htmlFor="curriculum-kind">Record type</label>
              <Select
                id="curriculum-kind"
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              >
                {kinds.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </Select>
              <label htmlFor="curriculum-entity">Local record</label>
              <Select
                id="curriculum-entity"
                value={entityID}
                onChange={(event) => chooseEntity(event.target.value)}
                disabled={rows.isLoading}
              >
                <option value="">Select a local record</option>
                {(rows.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name || "Name unavailable"}
                  </option>
                ))}
              </Select>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {(
                  [
                    ["standardID", "Curriculum standard"],
                    ["standardVersion", "Curriculum version"],
                    ["gradeCode", "Grade code"],
                    ["subjectCode", "Subject code"],
                    ["outcomeCode", "Outcome code"],
                    ["templateKey", "Template key"],
                    ["templateItemID", "Template item ID"],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key}>
                    <label htmlFor={`mapping-${key}`}>{label}</label>
                    <Input
                      id={`mapping-${key}`}
                      value={mapping[key] ?? ""}
                      required={
                        ["standardID", "standardVersion"].includes(key) ||
                        (key === "gradeCode" &&
                          [
                            "QualificationLevel",
                            "QualificationProgramLevel",
                            "Assessment",
                            "AssessmentGroup",
                            "QualificationModule",
                          ].includes(kind)) ||
                        (key === "subjectCode" &&
                          [
                            "Module",
                            "Assessment",
                            "AssessmentGroup",
                            "QualificationModule",
                          ].includes(kind))
                      }
                      maxLength={128}
                      disabled={!canMap || !entityID}
                      onChange={(event) =>
                        setMapping((current) => ({
                          ...current,
                          [key]: event.target.value || null,
                        }))
                      }
                    />
                  </div>
                ))}
                <div>
                  <label htmlFor="mapping-templateRevision">
                    Template revision
                  </label>
                  <Input
                    id="mapping-templateRevision"
                    type="number"
                    min={1}
                    step={1}
                    value={mapping.templateRevision ?? ""}
                    disabled={!canMap || !entityID}
                    onChange={(event) =>
                      setMapping((current) => ({
                        ...current,
                        templateRevision: event.target.value
                          ? Number(event.target.value)
                          : null,
                      }))
                    }
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="mt-4"
                loading={saveMapping.busy}
                disabled={!canMap || !entityID}
              >
                Save curriculum mapping
              </Button>
            </form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <h2 className="mb-4 text-lg font-semibold">Reporting measure</h2>
            {!canReadReports ? (
              <p>
                You need report read permission to configure and inspect
                reporting measures.
              </p>
            ) : (
              <>
                <label htmlFor="reporting-measure">Saved measure</label>
                <Select
                  id="reporting-measure"
                  value={measureID ?? ""}
                  onChange={(event) => chooseMeasure(event.target.value)}
                >
                  <option value="">New measure</option>
                  {(measures.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </Select>
                <form
                  className="mt-4 space-y-3"
                  onChange={() => setMeasureDirty(true)}
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveMeasure.run();
                  }}
                >
                  <div>
                    <label htmlFor="measure-name">Measure name</label>
                    <Input
                      id="measure-name"
                      required
                      maxLength={200}
                      value={measure.name}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({ ...measure, name: event.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor="measure-offering">Subject offering</label>
                    <Select
                      id="measure-offering"
                      value={measure.qualificationModuleID}
                      required
                      disabled={!canWriteReports || !!measureID}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          qualificationModuleID: event.target.value,
                        })
                      }
                    >
                      <option value="">Select subject offering</option>
                      {(offerings.data ?? []).map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name || "Name unavailable"}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="measure-type">Stored result level</label>
                    <Select
                      id="measure-type"
                      value={measure.entityType}
                      disabled={!canWriteReports || !!measureID}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          entityType: event.target.value,
                          entityID: "",
                        })
                      }
                    >
                      {[
                        "Assessment",
                        "AssessmentGroup",
                        "QualificationModule",
                      ].map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="measure-target">Selected result</label>
                    <Select
                      id="measure-target"
                      value={measure.entityID}
                      required
                      disabled={
                        !canWriteReports || !!measureID || targets.isLoading
                      }
                      onChange={(event) =>
                        setMeasure({ ...measure, entityID: event.target.value })
                      }
                    >
                      <option value="">Select mapped result</option>
                      {(targets.data ?? [])
                        .filter((row) => row.curriculum.standardID)
                        .map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name || "Name unavailable"}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="measure-markType">Mark type</label>
                    <Select
                      id="measure-markType"
                      value={measure.markTypeID}
                      required
                      disabled={!canWriteReports || !!measureID}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          markTypeID: event.target.value,
                        })
                      }
                    >
                      <option value="">Select mark type</option>
                      {(markTypes.data ?? []).map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="measure-comparison">Comparison</label>
                    <Select
                      id="measure-comparison"
                      value={measure.comparisonKind}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          comparisonKind: event.target.value,
                        })
                      }
                    >
                      <option value="LocalAttainment">Local attainment</option>
                      <option value="CommonBenchmark">Common benchmark</option>
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="measure-definition">
                      Comparison definition ID
                    </label>
                    <p className="mb-1 text-sm text-text-weak">
                      Use the same comparison code across organisations whose
                      results should be combined, for example
                      grade5-maths-term1.
                    </p>
                    <Input
                      id="measure-definition"
                      required
                      maxLength={128}
                      value={measure.comparisonDefinitionID}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          comparisonDefinitionID: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor="measure-version">Comparison version</label>
                    <Input
                      id="measure-version"
                      required
                      maxLength={128}
                      value={measure.comparisonVersion}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          comparisonVersion: event.target.value,
                        })
                      }
                    />
                  </div>
                  <label className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={measure.finalizedOnly}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          finalizedOnly: event.target.checked,
                        })
                      }
                    />
                    Finalized results only
                  </label>
                  <label className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={measure.enabled}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          enabled: event.target.checked,
                        })
                      }
                    />
                    Measure enabled
                  </label>
                  <div>
                    <label htmlFor="measure-moderation">
                      Moderation policy
                    </label>
                    <Select
                      id="measure-moderation"
                      value={measure.moderation}
                      disabled={!canWriteReports}
                      onChange={(event) =>
                        setMeasure({
                          ...measure,
                          moderation: event.target.value,
                        })
                      }
                    >
                      <option value="Any">Any moderation status</option>
                      <option value="Moderated">Approved results only</option>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    loading={saveMeasure.busy}
                    disabled={!canWriteReports}
                  >
                    Save reporting measure
                  </Button>
                </form>
                {measureID && (
                  <div className="mt-6 border-t border-border pt-4">
                    <h3 className="mb-2 font-semibold">Automatic snapshots</h3>
                    <p className="mb-3 text-sm text-text-weak">
                      Enabled measures publish changed stored results after
                      calculation runs, including automatic mark imports, and
                      after finalization or moderation. Saving a measure also
                      publishes its current results. Unchanged runs reuse the
                      latest snapshot. Missing or excluded learners remain in
                      coverage. Sync published results in Bloom to update its
                      reports.
                    </p>
                    {measureDirty && (
                      <p className="mb-3 text-sm text-text-weak">
                        Save measure changes to apply them to automatic
                        snapshots.
                      </p>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => {
                        history.refresh();
                      }}
                    >
                      Reload snapshot history
                    </Button>
                    <ul className="mt-3 space-y-2">
                      {(history.data ?? []).map((row) => (
                        <li key={row.snapshotID}>
                          <a
                            href={store.snapshotURL(row.snapshotID)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-text underline"
                          >
                            Snapshot {row.revision} — {row.enrollments.length}{" "}
                            eligible learners
                          </a>
                          <span className="ml-2 text-sm text-text-weak">
                            {
                              row.enrollments.filter(
                                (item) => item.result !== null,
                              ).length
                            }{" "}
                            results
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </PageContainer>
  );
}
